import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { CreditCard as CardIconCreditCard } from 'lucide-react';
import { Button, FormErrorSummary, Input, Select, Switch, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { cardsApi, getApiError, type CardPolicy } from '../../lib/api.js';
import { PageHeader } from '../../app/page-title.js';

export function CompanyCardSettings({
  unitName,
  unitId,
}: {
  unitName: string | null;
  unitId: string | null;
}) {
  const [scope, setScope] = useState<'COMPANY' | 'UNIT'>(unitName ? 'UNIT' : 'COMPANY');
  const query = useQuery({
    queryKey: ['company', 'card-policy', scope, unitId],
    queryFn: () => cardsApi.policy(scope),
    retry: false,
  });
  const client = useQueryClient();
  return (
    <section>
      <PageHeader
        trail="Configurações / Cartão e taxas"
        title="Cartão e taxas"
        description="Limite de parcelas e condições da operadora para referência financeira. O caixa informa somente as parcelas."
      />
      <div className="grid gap-4">
        <Select
          label="Configurar"
          value={scope}
          onValueChange={(value) => setScope(value as typeof scope)}
          options={[
            { value: 'COMPANY', label: 'Padrão da empresa' },
            ...(unitName ? [{ value: 'UNIT', label: `Unidade: ${unitName}` }] : []),
          ]}
        />
        {query.isLoading ? (
          <LoadingCard label="Carregando configuração do cartão" />
        ) : query.error ? (
          <ErrorCard message={getApiError(query.error)} />
        ) : query.data ? (
          <CardPolicyForm
            key={`${scope}:${unitId}:${query.data.versionId}:${query.data.inherited}`}
            policy={query.data.policy}
            scope={scope}
            inherited={query.data.inherited}
            onSaved={async () => {
              await client.invalidateQueries({ queryKey: ['company', 'card-policy'] });
              await client.invalidateQueries({ queryKey: ['company', 'sale-card-policy'] });
            }}
          />
        ) : null}
      </div>
    </section>
  );
}
function CardPolicyForm({
  policy,
  scope,
  inherited,
  onSaved,
}: {
  policy: CardPolicy;
  scope: 'COMPANY' | 'UNIT';
  inherited: boolean;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState(policy);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const snackbar = useSnackbar();
  const patch = (value: Partial<CardPolicy>) => setDraft((current) => ({ ...current, ...value }));
  async function save(value: CardPolicy | null) {
    setSaving(true);
    setError(undefined);
    try {
      await cardsApi.save(scope, value);
      await onSaved();
      snackbar.success(
        value ? 'Configuração do cartão salva.' : 'Unidade usando o padrão da empresa.',
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  }
  const numeric = (
    key:
      | 'maxInstallments'
      | 'firstReceiptDays'
      | 'receiptIntervalDays'
      | 'anticipationDays'
      | 'anticipationPeriodDays',
    label: string,
    min: number,
    max: number,
  ) => (
    <Input
      label={label}
      className="w-28 max-w-full"
      type="number"
      min={min}
      max={max}
      step={1}
      error={
        !Number.isSafeInteger(draft[key]) || draft[key] < min || draft[key] > max
          ? `Use um inteiro de ${min} a ${max}.`
          : undefined
      }
      value={draft[key]}
      onChange={(event) => {
        const value = Number(event.target.value);
        patch(
          key === 'maxInstallments'
            ? {
                maxInstallments: value,
                feeBasisPoints: Array.from(
                  { length: Math.min(48, Math.max(0, value)) },
                  (_, index) => draft.feeBasisPoints[index] ?? null,
                ),
              }
            : { [key]: value },
        );
      }}
    />
  );
  return (
    <form
      id="card-policy-form"
      className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-4"
      onSubmit={(event) => {
        event.preventDefault();
        void save(draft);
      }}
    >
      <ContentCardHeader
        title="Condições do cartão"
        icon={<CardIconCreditCard className="size-4" />}
      />
      {scope === 'UNIT' ? (
        <p className="text-sm text-content-muted">
          {inherited
            ? 'Esta unidade usa o padrão da empresa. Salvar cria uma configuração própria.'
            : 'Esta unidade tem configuração própria.'}
        </p>
      ) : (
        <p className="text-sm text-content-muted">Aplicado às unidades sem configuração própria.</p>
      )}
      <div className="grid max-w-xl items-start gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <Input
          label="Operadora do cartão"
          error={
            !draft.operatorName.trim() && draft.feeBasisPoints.some((rate) => rate !== null)
              ? 'Informe a operadora.'
              : undefined
          }
          value={draft.operatorName}
          onChange={(event) => patch({ operatorName: event.target.value })}
        />
        {numeric('maxInstallments', 'Máximo de parcelas', 1, 48)}
      </div>
      <fieldset className="grid gap-3">
        <legend className="mb-2 font-semibold">Taxa por quantidade de parcelas</legend>
        <p className="text-sm text-content-muted">
          Percentual descontado pela operadora. Em branco: taxa não informada. Use 0 para taxa zero.
          As taxas não bloqueiam o parcelamento da venda.
        </p>
        <div className="grid grid-cols-[repeat(auto-fill,8rem)] gap-3">
          {draft.feeBasisPoints.map((rate, index) => (
            <Input
              key={index}
              label={`Taxa em ${index + 1}× (%)`}
              className="w-28 max-w-full"
              type="number"
              min={0}
              max={99.99}
              step="0.01"
              error={
                rate !== null && (!Number.isSafeInteger(rate) || rate < 0 || rate >= 10000)
                  ? 'Use uma taxa de 0 a 99,99%.'
                  : undefined
              }
              value={rate === null ? '' : rate / 100}
              onChange={(event) =>
                patch({
                  feeBasisPoints: draft.feeBasisPoints.map((current, position) =>
                    position === index
                      ? event.target.value === ''
                        ? null
                        : Math.round(Number(event.target.value) * 100)
                      : current,
                  ),
                })
              }
            />
          ))}
        </div>
      </fieldset>
      <div className="grid max-w-3xl items-start gap-4 sm:grid-cols-2">
        {numeric('firstReceiptDays', 'Primeiro recebimento após a venda (dias)', 0, 730)}
        {numeric('receiptIntervalDays', 'Intervalo entre recebimentos (dias)', 1, 365)}
      </div>
      <Switch
        checked={draft.anticipationEnabled}
        onChange={(value) => patch({ anticipationEnabled: value })}
      >
        Informar condições de antecipação
      </Switch>
      {draft.anticipationEnabled ? (
        <div className="grid max-w-3xl items-start gap-4 sm:grid-cols-2">
          {numeric('anticipationDays', 'Recebimento antecipado após a venda (dias)', 0, 730)}
          <Input
            label="Taxa de antecipação (%)"
            className="w-28 max-w-full"
            type="number"
            min={0}
            max={99.99}
            step="0.01"
            value={draft.anticipationBasisPoints / 100}
            onChange={(event) =>
              patch({ anticipationBasisPoints: Math.round(Number(event.target.value) * 100) })
            }
          />
          <Select
            label="Fórmula da antecipação"
            value={draft.anticipationFormula}
            onValueChange={(value) =>
              patch({ anticipationFormula: value as CardPolicy['anticipationFormula'] })
            }
            options={[
              { value: 'FLAT', label: 'Percentual fixo por parcela' },
              { value: 'PRO_RATA', label: 'Percentual proporcional aos dias antecipados' },
            ]}
          />
          {draft.anticipationFormula === 'PRO_RATA'
            ? numeric('anticipationPeriodDays', 'Base da taxa proporcional (dias)', 1, 365)
            : null}
          <p className="text-sm text-content-muted sm:col-span-2">
            Estas condições não solicitam antecipação nem alteram o registro da venda. O custo é
            calculado sobre cada parcela após a taxa do cartão.
          </p>
        </div>
      ) : null}
      {error ? <RequestAlert message={error} /> : null}
      <div className="flex flex-wrap items-center justify-end gap-3">
        <FormErrorSummary form="card-policy-form" />
        {scope === 'UNIT' && !inherited ? (
          <Button type="button" variant="outline" disabled={saving} onClick={() => void save(null)}>
            Usar padrão da empresa
          </Button>
        ) : null}
        <Button type="submit" disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar configuração'}
        </Button>
      </div>
    </form>
  );
}
